import { ConsoleLogger } from '@proposify/frontend-observability';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Document } from 'services/documents/Document';
import type { UpdateDocumentStatusPayload } from 'services/repositories/interfaces/EditorRepository';
import { QueryKeys } from 'utils/queryKeys';
import { ContentType } from 'utils/getContentType';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import type { GetDocumentById } from 'services/repositories/implementations/ApiDocumentServiceRepository';

interface UseUpdateDocumentStatusProps {
  contentId: string;
  onSuccess?: (newStatus: string) => void;
  onError?: () => void;
}

export const v3StatusMap = {
  Draft: 'DRAFT',
  Sent: 'SENT',
} as const;

export const useUpdateDocumentStatus = ({ contentId, onSuccess, onError }: UseUpdateDocumentStatusProps) => {
  const queryClient = useQueryClient();

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (payload: UpdateDocumentStatusPayload) => {
      const result = await apiDocumentServiceRepository.updateDocumentStatus(contentId, payload).catch((error) => {
        ConsoleLogger.error(error, {
          context: 'useUpdateDocumentStatus - document service call failed',
          contentId,
          payload,
        });
      });

      return result;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(
        QueryKeys.getDocumentMetadataByContentId(ContentType.DOCUMENT, contentId),
        (oldData: GetDocumentById | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            status: data?.data.status,
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: QueryKeys.getDocumentMetadataByContentId(ContentType.DOCUMENT, contentId),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.getDocumentApprovalStateBase(contentId),
      });

      data?.data?.status && onSuccess?.(data?.data?.status?.toLowerCase());
    },
    onError,
  });

  const updateStatus = (status: Document['status']) => {
    const payload: UpdateDocumentStatusPayload = { status: v3StatusMap[status] };
    mutate(payload);
  };

  return { updateStatus, isLoading };
};
